import { ArrowUpRight, BookOpen } from "lucide-react";
import ContentSection from "@/components/sections/ContentSection";
import { GuideDefinition, trackGuideDownload } from "@/lib/guides";

interface GuideDownloadSectionProps {
  guide: GuideDefinition;
  eyebrow?: string;
  heading: string;
  body: string;
  /** Analytics location label, e.g. "buy-a-home". */
  location: string;
  variant?: "light" | "dark";
}

const GuideDownloadSection = ({
  guide,
  eyebrow = "Free Guide",
  heading,
  body,
  location,
  variant = "light",
}: GuideDownloadSectionProps) => {
  const isDark = variant === "dark";

  return (
    <ContentSection variant={variant} className="py-16 md:py-20 lg:py-24">
      <div
        className={`mx-auto max-w-wide border p-8 md:p-12 ${
          isDark
            ? "border-secondary-foreground/15 bg-secondary-foreground/[0.04]"
            : "border-border bg-card"
        }`}
      >
        <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-[minmax(0,1fr)_auto] lg:gap-14">
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <BookOpen className="h-5 w-5 text-accent" strokeWidth={1.6} aria-hidden="true" />
              <span className="font-body text-xs uppercase tracking-[0.16em] text-accent">
                {eyebrow}
              </span>
            </div>
            <h2
              className={`font-headline text-3xl leading-[1.12] md:text-4xl ${
                isDark ? "text-secondary-foreground" : "text-foreground"
              }`}
            >
              {heading}
            </h2>
            <p
              className={`max-w-2xl text-base leading-[1.8] md:text-lg ${
                isDark ? "text-secondary-foreground/85" : "text-muted-foreground"
              }`}
            >
              {body}
            </p>
          </div>

          <div className="lg:shrink-0">
            <a
              href={guide.href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={guide.ariaLabel}
              onClick={() => trackGuideDownload(guide, location)}
              className="inline-flex items-center gap-2 bg-accent px-7 py-4 font-body text-xs uppercase tracking-[0.16em] text-accent-foreground transition-colors duration-300 hover:bg-accent/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            >
              {guide.cta}
              <ArrowUpRight className="h-4 w-4" strokeWidth={1.8} aria-hidden="true" />
            </a>
            <p
              className={`mt-3 text-xs ${
                isDark ? "text-secondary-foreground/70" : "text-muted-foreground"
              }`}
            >
              PDF · Opens in a new tab
            </p>
          </div>
        </div>
      </div>
    </ContentSection>
  );
};

export default GuideDownloadSection;
